import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

import en from "../locales/en.json";
import tr from "../locales/tr.json";
import es from "../locales/es.json";
import de from "../locales/de.json";
import it from "../locales/it.json";
import fr from "../locales/fr.json";

const translations = { en, tr, es, de, it, fr };

const LANGUAGE_KEY = "memeet_language";
const DEFAULT_LANGUAGE = "en";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(LANGUAGE_KEY);
      return saved && translations[saved] ? saved : DEFAULT_LANGUAGE;
    } catch {
      return DEFAULT_LANGUAGE;
    }
  });

  const setLanguage = useCallback((lang) => {
    if (!translations[lang]) return;
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, []);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
  }, [setLanguage]);

  const getTranslation = useCallback(
    (key, params = {}) => {
      const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
      let translation = currentTranslations[key] || key;

      // Parameter interpolation: {paramKey} replaced
      Object.keys(params).forEach((paramKey) => {
        translation = translation.replace(
          new RegExp(`\\{${paramKey}\\}`, "g"),
          params[paramKey]
        );
      });

      return translation;
    },
    [language]
  );

  const t = useCallback(
    (key, fallback, params = {}) => {
      const translated = getTranslation(key, params);
      if (!translated || translated === key) return fallback || key;
      return translated;
    },
    [getTranslation]
  );

  const availableLanguages = [
    { code: "en", name: "English" },
    { code: "tr", name: "Türkçe" },
    { code: "es", name: "Espanol" },
    { code: "de", name: "Deutsch" },
    { code: "it", name: "Italiano" },
    { code: "fr", name: "Francais" },
  ];

  const value = {
    language,
    setLanguage,
    changeLanguage,
    getTranslation,
    t,
    availableLanguages,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
